import { useEffect, useState, useCallback } from "react";
import { useQuery } from "graphql-hooks";
import { Col, Row, Button } from 'reactstrap';
import { useFetchWrapper } from '../../hooks/useFetchWrapper'
import { Loading } from "./Loading";
import { clientConfig } from "../../utilities/configService";
import { ContentBlock } from "./Content/ContentBlock";
import { ContentHeading } from "./Content/ContentHeading";
import { ContentBody } from "./Content/ContentBody";
import { useNavigate } from 'react-router-dom';
import { useOidc } from "@axa-fr/react-oidc";
import { useAPIErrorContext } from '../../contexts/apiErrorContext';
import parse from 'html-react-parser';

const CMS_QUERY = `query dataQuery {
  pageHomePage {
    fieldSampleFleetsSectionTitle
    fieldSampleFleetsBody
    fieldSampleFleetsEndnote
  }
}`;

interface IDefaultFleet {
    annualMaintenanceSavings: number,
    fleetID: number,
    fleetName: string,
    organizationTypeName: string,
    imagePath: string,
    lifetimeCO2Reductions: number,
    totalVehicleCount: number
    vehicleSets: IVehicleSet[]
}

interface IVehicleSet {
    fleetID: string,
    vehicleSecondaryTypeID: number,
    vehicleSecondaryTypeName: string,
    vehicleSetVehicleCount: number,
    vehicleTypeID: number,
    imageID: number,
    imagePath: string,
    vehicleTypeName: string
}


export const InstantEstimates = () => {

    const fetchWrapper = useFetchWrapper();
    const navigate = useNavigate();
    const { isAuthenticated } = useOidc();
    const { loading, error, data } = useQuery(CMS_QUERY, {
        variables: {
            limit: 10
        }
    });
    const { trackError } = useAPIErrorContext();

    const [defaultFleets, setDefaultFleets] = useState<IDefaultFleet[]>([]);

    const applicationName = clientConfig?.APPLICATION_NAME || '';
    const getDefaultFleets = useCallback(() => {
        fetchWrapper.get(`fleet/defaultfleets/${applicationName}`)
            .then((data) => {
                if (data.success && data.defaultFleets) {
                    setDefaultFleets(data.defaultFleets);
                }
            });
    }, [applicationName]);

    useEffect(() => {
        getDefaultFleets();
    }, [getDefaultFleets]);

    if (!error && !loading) {
        return (
            <ContentBlock className="instantEstimate">
                <ContentHeading title={data.pageHomePage.fieldSampleFleetsSectionTitle} />
                <ContentBody>
                    <div className="instantEstSection">
                        <div className="instandEstimateBody" dangerouslySetInnerHTML={{ __html: data.pageHomePage.fieldSampleFleetsBody }} />
                        <Row className="instantEstCardContainer">
                            {defaultFleets.sort((a, b) => a.totalVehicleCount - b.totalVehicleCount).map((item: IDefaultFleet, index: number) => {
                                var costReduced = item.annualMaintenanceSavings.toLocaleString('en-US');
                                if (item.annualMaintenanceSavings <= 999) {
                                    costReduced = item.annualMaintenanceSavings.toFixed(0);
                                }
                                else if (item.annualMaintenanceSavings >= 1000 && item.annualMaintenanceSavings <= 999999) {
                                    costReduced = (item.annualMaintenanceSavings / 1000).toFixed(0) + "K";

                                } else if (item.annualMaintenanceSavings >= 1000000) {
                                    costReduced = (item.annualMaintenanceSavings / 1000000).toFixed(1) + "M";
                                }
                                return (
                                    <Col key={index} lg={2} md={4} className="instantEstCard" onClick={() => {
                                        navigate("/viewestimate/" + item.fleetID);
                                    }}>
                                        {/*card title*/}
                                        <Row>
                                            <Col sm={12} className="instantEstCardHeader cardAccent1">
                                                <Row className="cardHeaderRow">
                                                    <Col lg={12} md={12} sm={12}>
                                                        <div className="instantEstCardHeaderContent">
                                                            <div className="instantEstCardHeaderText">
                                                                <h3 className="css_heading bold">{item.fleetName}</h3>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>

                                        {/*vehicle count*/}
                                        <Row>
                                            <Col sm={12} className="instantEstCardHeader cardAccent3">
                                                <Row className="cardHeaderRow">
                                                    <Col lg={12} md={12} sm={12}>
                                                        <div className="instantEstCardHeaderContent">
                                                            <div className="instantEstCardHeaderText">
                                                                <span className="vehicleCount">{item.totalVehicleCount}</span>
                                                                <br />
                                                                VEHICLES
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>

                                        {/*costs reduced*/}
                                        <Row>
                                            <Col sm={12} className="instantEstCardHeader cardAccentWhite">
                                                <div className="cardHeaderRow">
                                                    <Col lg={8} md={8} sm={8} className={'cardHeaderRowCol'}>
                                                        <div className="instantEstCardHeaderContent">
                                                            <div className="instantEstCardHeaderText">
                                                                COSTS REDUCED
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg={4} md={4} sm={4} className={'cardHeaderRowCol'}>
                                                        <div className="instantEstCardHeaderContent">
                                                            <div className="instantEstCardHeaderText cardCost">
                                                                <span className="instantEstLeftCardBodyCostAndEmissionValues"><span className="cardThemeFontColor4">${costReduced}</span></span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/*co2 reduced*/}
                                        <Row>
                                            <Col sm={12} className="instantEstCardHeader cardAccentWhite">
                                                <div className="cardHeaderRow">
                                                    <Col lg={7} md={6} sm={6} className={'cardHeaderRowCol'}>
                                                        <div className="instantEstCardHeaderContent">
                                                            <div className="instantEstCardHeaderText">
                                                                CO2 REDUCED
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg={5} md={6} sm={6} className={'cardHeaderRowCol'}>
                                                        <div className="instantEstCardHeaderContent">
                                                            <div className="instantEstCardHeaderText cardCost">
                                                                <span className="instantEstLeftCardBodyCostAndEmissionValues"><span className="cardThemeFontColor4"> {item.lifetimeCO2Reductions.toLocaleString('en-US')} MT</span></span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </div>
                                            </Col>
                                        </Row>

                                        {/*view estimate*/}
                                        <Row>
                                            <Col sm={12} className="instantEstCardHeader cardAccent1">
                                                <Row className="cardHeaderRow">
                                                    <Col lg={12} md={12} sm={12}>
                                                        <div className="instantEstCardHeaderContent">
                                                            <div className="instantEstCardHeaderText">
                                                                <span className="iconRightArrow estimate">VIEW ESTIMATE</span>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                );
                            })
                            }
                        </Row>
                        <Row>
                            <Col sm={12}>
                                <Row className="footerText">
                                    {isAuthenticated ? (
                                        <Col lg={12} md={12} sm={12}>
                                            <div className="instantEstFooterText">
                                                Or, create an estimate based on your fleet with our Fleet Estimator.
                                            </div>
                                            <Button className="solidNeg primaryButton text-nowrap btn btn-secondary iconRightArrow" href="/CreateFleet">CREATE A FLEET</Button>
                                        </Col>
                                    ) : (
                                        <Col lg={12} md={12} sm={12}>
                                            <div className="instantEstFooterText">
                                                Or, access our Fleet Estimator to create an estimate based on your fleet.
                                            </div>
                                                <Button className="solidNeg primaryButton text-nowrap btn btn-secondary iconRightArrow" href="/Login">{clientConfig?.CONTENT.INSTANT_ESTIMATE_BUTTON ? parse(clientConfig.CONTENT.INSTANT_ESTIMATE_BUTTON as string) : 'Get Access'}</Button>
                                        </Col>
                                    )}
                                </Row>
                            </Col>
                        </Row>
                    </div>
                </ContentBody>
            </ContentBlock>
        );
    }
    else if (!error) {
        return (<Loading showContent={false} includeHero={false} />);
    } else {
        trackError("Dato error", JSON.stringify(error));
        return(<></>);
    }
};
